import React, { useContext, useEffect, useMemo, useState } from "react";
import { SiteContext } from "../utils/SiteContext";
import { GetDownload, GetData, GetImportTime } from "../utils/AxiosUtils";
import {
  Box,
  Tooltip,
  useMediaQuery,
  Button,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import SearchBar from "../site components/SearchBar";
import CustomToolbar from "../components/CustomGridToolBar";
import MobileCardList from "./MobileCardList";

export default function UniversalDataGrid({
  source,
  endpoint,
  downloadKey,
  columns,
  mobileColumns,
  searchLabel,
  customSortField,
  onRowClick,
  tooltipFields = [],
  initiallyHiddenColumns = [],
  mobilePrimaryFields = [], // optional: pick which columns appear at top of each card
}) {
  const isMobile = useMediaQuery("(max-width: 600px)");

  const { loading } = useContext(SiteContext);
  const [loadingVal, setLoading] = loading;

  const [data, setData] = useState({});
  const [searchVal, setSearch] = useState("");
  // const [pageVal, setPage] = useState({ page: 1, pg_size: 100000, count: 0 });

  const [timestamp, setTimestamp] = useState(null);

  useEffect(() => {
      GetImportTime(source, setTimestamp);
  }, []);

  useEffect(() => {
    setLoading(false);
    // GetData(endpoint, searchVal, setData, [pageVal, setPage]);
        GetData(endpoint, searchVal, setData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingVal]);

  // --- Preserve your tooltip + sort comparator behavior for the grid view ---
  const customComparator = (v1, v2, param1, param2) => {
    let primaryCompare;
    if (typeof v1 === "number" && typeof v2 === "number") {
      primaryCompare = v1 - v2;
    } else if (Date.parse(v1) && Date.parse(v2)) {
      primaryCompare = new Date(v1) - new Date(v2);
    } else {
      primaryCompare = String(v1).localeCompare(String(v2));
    }
    if (primaryCompare !== 0) return primaryCompare;

    const row1 = param1.api.getRow(param1.id);
    const row2 = param2.api.getRow(param2.id);
    return (row1[customSortField] || "").localeCompare(row2[customSortField] || "");
  };

  const enhancedColumns = useMemo(
    () =>
      (columns || []).map((col) => {
        const shouldApplyTooltip = tooltipFields.includes(col.field);

        return {
          ...col,
          renderCell:
            shouldApplyTooltip && !col.renderCell
              ? (params) => (
                  <Tooltip title={params.value || ""} arrow>
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        width: "100%",
                      }}
                    >
                      {params.value}
                    </span>
                  </Tooltip>
                )
              : col.renderCell, // preserve existing
          sortComparator:
            typeof col.sortComparator === "function"
              ? col.sortComparator
              : col.sortComparator === true
              ? customComparator
              : col.sortComparator,
        };
      }),
    [columns, tooltipFields]
  );

  // Column visibility model for desktop grid
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() =>
    Object.fromEntries(initiallyHiddenColumns.map((field) => [field, false]))
  );

  // const rows = data?.results || [];
  const rows = data;

  return (
    <Box sx={{ height: `calc(100vh - 110px)`, display: "flex", flexDirection: "column" }}>

      {timestamp && isMobile && (
          <Box mb={1} display="flex" justifyContent="flex-end">
              <Box fontSize="0.85rem" color="text.secondary">
                  Last updated:&nbsp;
                  {new Date(timestamp).toLocaleString()}
              </Box>
          </Box>
      )}

      {/* Keep your search + export on both views */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <SearchBar search={[searchVal, setSearch]} label={searchLabel} />
      </Box>

      {timestamp &&!isMobile && (
          <Box display="flex" justifyContent="flex-end">
              <Box fontSize="0.85rem" color="text.secondary">
                  Last updated:&nbsp;
                  {new Date(timestamp).toLocaleString()}
              </Box>
          </Box>
      )} 

      <Box
        sx={{
          height: "100%",
          width: "100%",
          mt: 2,
          backgroundColor: isMobile ? "none" : "white",
          overflowX: isMobile ? "clip" : "visible",
          p: 0,
        }}
      >
        {isMobile ? (
          loadingVal ? (
            <div style={{ padding: 16 }}>Rows Loading...</div>
          ) : (
            <MobileCardList
              rows={rows}
              columns={mobileColumns}
              tooltipFields={tooltipFields}
              initiallyHiddenColumns={initiallyHiddenColumns}
              onRowClick={onRowClick}
              mobilePrimaryFields={mobilePrimaryFields}
            />
          )
        ) : (
          <DataGrid
            rows={rows}
            columns={enhancedColumns}
            getRowId={(row) => row.id}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            components={{
              Toolbar: () => (
                <CustomToolbar onExport={() => GetDownload(searchVal, downloadKey)} />
              ),
            }}
            disableSelectionOnClick
            onRowClick={onRowClick}
            rowHeight={35}
            sx={{ p: 2 }}
            localeText={{
              noRowsLabel: "Rows Loading...",
            }}
          />
        )}
      </Box>
    </Box>
  );
}
